/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { CleanIconButton, LoadingSpinner, Tooltip } from '@neo4j-ndl/react';
import { XMarkIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';

import { type CommonProps } from '../../_common/types';

type ImageTagProps = {
  /** The source of the image */
  src: string;
  /** The alt text of the image */
  alt: string;
  /** The name of the image */
  fileName: string;
  /** The type of the image, e.g. 'jpg', 'png', 'gif', etc. */
  fileType: string;
  /** Callback function triggered either when the image tag is clicked, or when the remove button is clicked (if isRemovable is true) */
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /** Whether the image tag is removable */
  isRemovable?: boolean;
  /** Whether the image tag is loading */
  isLoading?: boolean;
};

/**
 * The component is used to display an image in a chat, or in a prompt.
 * It can be removable and it can be loading.
 *
 * @alpha - Changes to this component may be breaking.
 */
const ImageTag = ({
  src,
  fileName,
  fileType,
  alt,
  onClick,
  isRemovable = false,
  isLoading = false,
  ref,
  className,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<React.ElementType, ImageTagProps>) => {
  const Component = isRemovable !== true ? 'button' : 'div';
  const classes = classNames('ndl-ai-image-tag', className);

  const image = `${fileName}.${fileType}`;

  return (
    <Tooltip
      type="simple"
      placement="bottom"
      hoverDelay={{ close: 0, open: 500 }}
    >
      <Tooltip.Trigger hasButtonWrapper>
        <Component
          ref={ref}
          className={classes}
          style={style}
          {...(isRemovable ? {} : { onClick })}
          // what is read by screen readers here?
          {...restProps}
          {...htmlAttributes}
        >
          <img src={src} alt={alt} />
          {isRemovable && (
            <>
              <div className="ndl-ai-image-tag-remove-wrapper" />
              <CleanIconButton
                onClick={onClick}
                description={null}
                htmlAttributes={{
                  'aria-label': `Remove ${image}`,
                }}
                size="small"
                className="ndl-ai-image-tag-remove"
              >
                <XMarkIconOutline />
              </CleanIconButton>
            </>
          )}
          {isLoading && (
            <div className="ndl-ai-image-tag-loading-overlay">
              <LoadingSpinner size="medium" />
            </div>
          )}
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content>{image}</Tooltip.Content>
    </Tooltip>
  );
};

export { ImageTag };
